import { alpha, createTheme } from "@material-ui/core/styles";

import palette from "./palette";

const borderRadius = {
  small: "10px",
  medium: "16px",
  input: "12px",
  table: "14px",
  pill: "999px",
};

const fontFamily = [
  '"Inter"',
  '"Roboto"',
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(",");

const createAppTheme = (type) => {
  const colors = palette[type];
  const isDark = type === "dark";
  const { palette: basePalette } = createTheme({ palette: { type, ...colors } });
  const shadowColor = (opacity) =>
    alpha(isDark ? "#000000" : colors.brand.navy, isDark ? opacity * 3 : opacity);

  // Teal sobre fundo claro não tem contraste de texto: usa a versão escura.
  const accentText = isDark ? colors.brand.teal : colors.brand.tealDark;
  const hoverTeal = "#06AD8E";

  return createTheme({
    palette: {
      type,
      ...colors,
      tintedBackground: alpha(colors.primary.main, isDark ? 0.12 : 0.08),
      accentText,
    },

    typography: {
      fontFamily,
      h4: { fontWeight: 700, letterSpacing: "-0.01em" },
      h5: { fontWeight: 700, letterSpacing: "-0.01em" },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: "none" },
      sectionLabel: {
        fontSize: "11px",
        fontWeight: 700,
        lineHeight: 1.4,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: basePalette.text.secondary,
      },
    },

    borderRadius,

    sizing: {
      appBar: 64,
    },

    border: {
      divider: `1px solid ${colors.divider}`,
      ring: `0 0 0 2px ${colors.background.paper}`,
    },

    elevation: {
      light: `0 1px 2px ${shadowColor(0.08)}, 0 4px 14px -4px ${shadowColor(0.12)}`,
      card: `0 1px 2px ${shadowColor(0.06)}, 0 8px 24px -12px ${shadowColor(0.16)}`,
    },

    scrollbarStyles: {
      "&::-webkit-scrollbar": {
        width: "8px",
        height: "8px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: colors.scrollbar.thumb,
        borderRadius: "8px",
      },
    },

    overrides: {
      MuiCssBaseline: {
        "@global": {
          body: {
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
        },
      },
      MuiButton: {
        root: {
          borderRadius: borderRadius.small,
          fontSize: "13px",
          textTransform: "none",
          fontWeight: 600,
          padding: "6px 16px",
        },
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
        containedPrimary: {
          "&:hover": { backgroundColor: hoverTeal },
        },
        outlined: {
          backgroundColor: colors.cards.background,
          borderColor: colors.divider,
        },
        outlinedPrimary: {
          color: accentText,
          borderColor: alpha(accentText, 0.5),
          "&:hover": {
            backgroundColor: alpha(colors.primary.main, 0.1),
            borderColor: accentText,
          },
        },
        textPrimary: {
          color: accentText,
          "&:hover": { backgroundColor: alpha(colors.primary.main, 0.1) },
        },
      },
      MuiIconButton: {
        colorPrimary: { color: accentText },
      },
      MuiTypography: {
        colorPrimary: { color: accentText },
      },
      MuiLink: {
        root: { color: accentText },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: borderRadius.input,
          backgroundColor: colors.cards.background,
        },
        notchedOutline: {
          borderColor: colors.divider,
        },
      },
      MuiFormLabel: {
        root: {
          "&$focused": { color: accentText },
        },
      },
      MuiMenu: {
        paper: {
          borderRadius: borderRadius.input,
          boxShadow: `0 8px 30px -8px ${shadowColor(0.35)}`,
        },
      },
      MuiDialog: {
        paper: {
          borderRadius: borderRadius.medium,
        },
      },
      MuiDialogTitle: {
        root: {
          color: colors.text.primary,
          fontWeight: 700,
        },
      },
      MuiPaper: {
        rounded: {
          borderRadius: borderRadius.small,
        },
        elevation1: {
          boxShadow: `0 1px 2px ${shadowColor(0.06)}, 0 6px 20px -10px ${shadowColor(0.18)}`,
        },
        outlined: {
          "&$rounded": {
            borderRadius: borderRadius.medium,
          },
          borderColor: colors.divider,
        },
      },
      MuiTabs: {
        indicator: {
          height: 3,
          borderRadius: "3px 3px 0 0",
          backgroundColor: colors.primary.main,
        },
      },
      MuiTab: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          fontSize: "13px",
        },
      },
      MuiChip: {
        root: {
          borderRadius: borderRadius.small,
          fontWeight: 500,
        },
      },
      MuiTooltip: {
        tooltip: {
          backgroundColor: isDark ? "#163A55" : colors.brand.navy,
          color: "#FFFFFF",
          fontSize: "12px",
          borderRadius: "8px",
          padding: "6px 10px",
        },
        arrow: {
          color: isDark ? "#163A55" : colors.brand.navy,
        },
      },
      MuiTableCell: {
        head: {
          fontWeight: 700,
          fontSize: "12px",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: colors.text.secondary,
        },
      },
      MuiBadge: {
        colorPrimary: {
          backgroundColor: colors.brand.yellow,
          color: colors.brand.navy,
          fontWeight: 700,
        },
      },
      MuiAvatar: {
        colorDefault: {
          backgroundColor: alpha(colors.primary.main, isDark ? 0.25 : 0.18),
          color: isDark ? colors.text.primary : colors.brand.navy,
          fontWeight: 600,
        },
      },
    },
  });
};

export default createAppTheme;
